import React from "react";
import { styled } from "styled-components";

import type { MultiSelectField, MultiSelectOption } from "../types";
import { Button } from "../../Button";
import { Icon } from "../../Icon";
import { theme, useTheme } from "../../../theme";
import { Input } from "../../Input";
import { FieldGroup, HelperText, Label } from "../shared";

const OptionsList = styled.div({
  overflowY: "auto",
  border: `1px solid ${theme.colors.grayMed}`,
  borderRadius: theme.radii[1],
});

const OptionRow = styled.div<{
  $isActive: boolean;
}>(({ theme, $isActive }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: theme.space[3],
  borderBottom: `1px solid ${theme.colors.grayMed}`,
  cursor: "pointer",
  backgroundColor: $isActive ? theme.colors.primaryLightest : "none",
}));

const TagBar = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.space[2],
  marginBottom: theme.space[3],
});

/** Custom "multi-select" component that includes:
 * - Search Bar (optional)
 * - Filter buttons by tag/category
 * - List of options with check marks
 *
 * @param field - MultiSelect field configuration
 * @param selected - Array of currently selected option values (supports initial values)
 * @param onChange - Callback when selection changes
 */
export const MultiSelect: React.FC<{
  field: MultiSelectField;
  selected: Array<string | number>;
  onChange: (ids: Array<string | number>) => void;
}> = ({ field, selected, onChange }) => {
  const [search, setSearch] = React.useState("");
  const [activeTag, setActiveTag] = React.useState<string>("");
  const theme = useTheme();

  const tags = Array.from(
    new Set((field.options || []).flatMap((p) => p.tags || [])),
  ).sort();

  const filtered = (field.options || []).filter((p: MultiSelectOption) => {
    const byCat = activeTag ? p.tags?.includes(activeTag) : true;
    const bySearch = search
      ? p.label.toLowerCase().includes(search.toLowerCase())
      : true;
    return byCat && bySearch;
  });

  const toggleItem = (id: string | number) => {
    const exists = selected.includes(id);
    if (exists) {
      onChange(selected.filter((x) => x !== id));
    } else {
      const next = [...selected, id];
      if (field.maxSelected && next.length > field.maxSelected) return;
      onChange(next);
    }
  };

  return (
    <FieldGroup>
      {field.label && (
        <Label>
          {field.label}
          {field.required && (
            <span style={{ color: theme.colors.warn }}> *</span>
          )}
        </Label>
      )}
      {field.helpText && (
        <HelperText style={{ marginBottom: theme.space[3] }}>
          {field.helpText}
        </HelperText>
      )}

      {field.showSearch && (
        <Input
          size="md"
          value={search}
          placeholder="Search..."
          onValueChange={setSearch}
          style={{ marginBottom: theme.space[3], width: "100%" }}
          width="100%"
        />
      )}
      {tags.length > 0 && (
        <TagBar>
          <Icon
            name="filter"
            size={28}
            color={theme.colors.primary}
            style={{ margin: "auto" }}
          />
          {tags.map((c) => (
            <Button
              key={c}
              variant={activeTag === c ? "info" : "outline"}
              size="sm"
              onClick={() => setActiveTag(activeTag === c ? "" : c)}
            >
              {c}
            </Button>
          ))}
        </TagBar>
      )}

      <OptionsList>
        {filtered.map((p) => {
          const isSelected = selected.includes(p.value);
          return (
            <OptionRow
              $isActive={isSelected}
              key={String(p.value)}
              onClick={() => toggleItem(p.value)}
            >
              <span>{p.label}</span>
              {isSelected && (
                <Icon color={theme.colors.primary} name="checkmark" size={16} />
              )}
            </OptionRow>
          );
        })}
      </OptionsList>
      {filtered.length === 0 && (
        <HelperText style={{ marginBottom: theme.space[5] }}>
          No results
        </HelperText>
      )}
    </FieldGroup>
  );
};
